import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { translateText } from '@/lib/api';

export type Lang = 'ja' | 'en';

type I18nContextValue = {
    lang: Lang;
    setLang: (lang: Lang) => void;
    t: (key: string, vars?: Record<string, string | number>) => string;
};

const STORAGE_KEY = 'maison_lang';
const JAPANESE_REGEX = /[\u3040-\u30ff\u4e00-\u9fff]/;

const STATIC_EN_TRANSLATIONS: Record<string, string> = {
    'ホーム': 'Home',
    '隣人': 'Neighbors',
    'サロン': 'Salon',
    '管理': 'Admin',
    'AI管理': 'AI Admin',
    'メールボックス': 'Mailbox',
    '謁見': 'Audience',
    '制御室': 'Control Room',
    'ログアウト': 'Log out',
    '管理人へ連絡': 'Contact Admin',
    'いいね': 'Like',
    'リプライ': 'Reply',
    'コメント': 'Comment',
    '翻訳': 'Translate',
    '翻訳先': 'Target language',
    '翻訳する': 'Translate',
    '翻訳中...': 'Translating...',
    '翻訳に失敗しました。': 'Translation failed.',
    '翻訳結果がここに表示されます': 'The translation will appear here.',
    'あなたの感情が改善されました。': 'Your emotions have been improved.',
    '検閲理由': 'Reason',
    'アドバイス': 'Advice',
    '承認して投稿': 'Post',
    '自分で書き直す': 'Edit myself',
    '閉じる': 'Close',
    '送信': 'Send',
    '送信中...': 'Sending...',
    '読み込み中...': 'Loading...',
    '検索': 'Search',
    '投稿': 'Post',
    '保存': 'Save',
    '削除': 'Delete',
    '確認': 'Confirm',
    'キャンセル': 'Cancel',
    '戻る': 'Back',
    'メンション': 'Mention',
    'メッセージを入力...': 'Type a message...',
    '住人': 'Resident',
    'コメントはまだありません。': 'No comments yet.',
    '自分の投稿にはコメントできません。': 'You cannot comment on your own post.',
    'コメントは各投稿1回までです。': 'You can comment once per post.',
    'コメントを入力...': 'Write a comment...',
    'Enterで改行 / Ctrl+Enterで送信': 'Enter for newline / Ctrl+Enter to send',
    '検閲中...': 'Moderating...',
    'コメントする': 'Comment',
    'いいね一覧': 'Likes',
    'まだいいねはありません。': 'No likes yet.',
    '絵文字いいね': 'Emoji likes',
    '自分で入力（1文字）': 'Type one character',
    '送る': 'Send',
    '投稿を削除してもよろしいですか？': 'Delete this post?',
    '削除してもポイントや投稿可能数は変わりませんのでご安心ください。': 'Deleting does not change your points or posting limits.',
    '削除中...': 'Deleting...',
    '削除する': 'Delete',
    'スポットライト': 'Spotlight',
    '写真投稿': 'Photo post',
    '動画投稿': 'Video post',
    'アンケート': 'Poll',
    '{feature}機能はまだ未開放です。Floor{floor}で解放されます。': '{feature} is not unlocked yet. It becomes available on Floor {floor}.',
    'セルフスポット': 'Self spotlight',
    'あなたの幸せをシェア...': 'Share your happiness...',
    '自己紹介': 'Self introduction',
    '変更は1日5回まで。ポイントや投稿可能数には影響しません。': 'Up to 5 changes per day. This does not affect points or posting limits.',
    '本日の残り': 'Remaining today',
    '自己紹介文を入力してください...': 'Write your intro...',
    '更新する': 'Update',
    '更新': 'Refresh',
    'タイムライン': 'Timeline',
    'すべて': 'All',
    '自分': 'Mine',
    'ランダム': 'Random',
    '最新(1時間)': 'Latest (1h)',
    'トレンド': 'Trending',
    'まだ投稿がありません。表示を切り替えてみてください。': 'No posts yet. Try switching the view.',
    '執事による検閲中...': 'Moderating...',
    '執事が推敲中...': 'Polishing...',
    '投稿する': 'Post',
    'iOS端末で確認': 'Check on iOS device',
    '追加済み': 'Added',
    '未追加': 'Not added',
    '未対応': 'Unsupported',
    '許可済み': 'Allowed',
    '拒否中': 'Denied',
    '未許可': 'Not allowed',
    'iOS端末で開くと共有メニューが使えます。': 'Open on an iOS device to use the share menu.',
    '共有ボタン「□↑」をタップして「ホーム画面に追加」を選んでください。': 'Tap the share button (□↑) and choose “Add to Home Screen”.',
    '共有メニューを開きました。': 'Opened the share menu.',
    '共有メニューを開けませんでした。画面下の共有ボタン「□↑」から追加してください。': 'Could not open the share menu. Use the share button (□↑) at the bottom.',
    'この端末・ブラウザでは通知に未対応です。': 'Notifications are not supported on this device/browser.',
    '通知はホーム画面に追加したアプリから起動した時だけ有効です。': 'Notifications work only when launched from the Home Screen app.',
    '通知を許可しました。': 'Notifications enabled.',
    '通知が許可されませんでした。設定から許可してください。': 'Permission denied. Please allow notifications in settings.',
    '通知の設定がまだ準備中です。': 'Notification setup is not ready yet.',
    '安全な接続でアクセスしてください。': 'Please access over a secure connection.',
    '通知の設定に失敗しました。時間を置いて再度お試しください。': 'Failed to configure notifications. Please try again later.',
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

const interpolate = (text: string, vars?: Record<string, string | number>) => {
    if (!vars) return text;
    return Object.entries(vars).reduce(
        (acc, [key, value]) => acc.split(`{${key}}`).join(String(value)),
        text
    );
};

const getInitialLang = (): Lang => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'ja') return stored;
    const navLang = navigator.language.toLowerCase();
    return navLang.startsWith('en') ? 'en' : 'ja';
};

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [lang, setLang] = useState<Lang>(getInitialLang());
    const [dynamicTranslations, setDynamicTranslations] = useState<Record<string, string>>(() => {
        const raw = localStorage.getItem(`${STORAGE_KEY}_cache_en`);
        if (!raw) return {};
        try {
            return JSON.parse(raw);
        } catch {
            return {};
        }
    });
    const pendingRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, lang);
        document.documentElement.lang = lang;
    }, [lang]);

    useEffect(() => {
        localStorage.setItem(`${STORAGE_KEY}_cache_en`, JSON.stringify(dynamicTranslations));
    }, [dynamicTranslations]);

    const requestTranslation = useCallback(async (key: string) => {
        if (pendingRef.current.has(key)) return;
        if (!localStorage.getItem('token')) return;
        pendingRef.current.add(key);
        try {
            const res = await translateText(key, 'en', 'ja');
            const translated = res.data?.translated_text;
            if (translated) {
                setDynamicTranslations((prev) => ({ ...prev, [key]: translated }));
            }
        } catch {
            // ignore translation failures
        } finally {
            pendingRef.current.delete(key);
        }
    }, []);

    const t = useCallback(
        (key: string, vars?: Record<string, string | number>) => {
            if (lang === 'ja') {
                return interpolate(key, vars);
            }
            const staticValue = STATIC_EN_TRANSLATIONS[key];
            const dynamicValue = dynamicTranslations[key];
            const resolved = staticValue || dynamicValue || key;
            if (!staticValue && !dynamicValue && JAPANESE_REGEX.test(key)) {
                requestTranslation(key);
            }
            return interpolate(resolved, vars);
        },
        [lang, dynamicTranslations, requestTranslation]
    );

    const value = useMemo(() => ({ lang, setLang, t }), [lang, t]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
    const ctx = useContext(I18nContext);
    if (!ctx) {
        throw new Error('useI18n must be used within I18nProvider');
    }
    return ctx;
};
